import React from 'react';
import axios from "axios";
import PropTypes from 'prop-types';

import ColAtm from "../../../../../atomic/Col/Col";
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import LabelAtm from "../../../../../atomic/Label/Label";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import {ROOM_PHOTO_UPDATE, ROOM_PHOTO_ORDER} from "../../../../../../constants/api.constants";
import {getAppLang, getProperty, getUserToken} from "../../../../../../utils";
import {useTranslation} from "react-i18next";
import {AllCheckerCheckbox, CheckboxGroup} from "@createnl/grouped-checkboxes";
import MultiPhotoSelect from "../../../../../molecules/MultiPhotoSelect/MultiPhotoSelect";
import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    rectSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates
} from "@dnd-kit/sortable";
import SortablePhotoSelect from "../../../../../molecules/PhotoSelect/SortablePhotoSelect";
import useFlipOnReorder from "../../../../../../utils/useFlipOnReorder";

const EMPTY_PHOTOS = [];

const RoomList = props => {
    const {rooms, roomPhotoList, getRoomPhotoList, roomPhotoListFetching} = props;
    const [expanded, setExpanded] = React.useState(false);
    // Seçili fotoğrafların kullanıcı tarafından belirlenen sırası (id listesi).
    // Boşken fotoğraflar API'den geldiği sırada gösterilir.
    const [photoOrder, setPhotoOrder] = React.useState([]);
    // Seçim state'i; checkbox'lar buna bağlı (controlled). Seçim değişince fotoğraf
    // sıralanabilir grup ile sabit grup arasında anında yer değiştirir.
    const [selectedIds, setSelectedIds] = React.useState(() => new Set());
    const { t } = useTranslation();

    const sensors = useSensors(
        // 8px eşiği olmadan sürükleme, fotoğrafa tıklayıp seçme davranışını yutar
        useSensor(MouseSensor, {activationConstraint: {distance: 8}}),
        // Sürükleme tutamaçtan yapılıyor (touch-action:none), scroll çakışması yok;
        // bu yüzden çok kısa bir gecikme yeterli — sadece kazara dokunuşu ayıklar, hız hissi verir.
        useSensor(TouchSensor, {activationConstraint: {delay: 50, tolerance: 8}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    );

    const photos = roomPhotoList && roomPhotoList.get_room_photos ? roomPhotoList.get_room_photos : EMPTY_PHOTOS;

    // Önerilenler kutusu açıkken önerilen fotoğraflar ayrı blokta listelendiği için ana listeden düşer
    const mainPhotos = React.useMemo(() => (
        roomPhotoList.show_recommended === true
            ? photos.filter( (photo) => photo.is_recommended === false )
            : photos
    ), [photos, roomPhotoList.show_recommended]);

    // Gruplama API'den gelen is_selected'a değil, canlı seçim state'ine bakar
    const selectedPhotos = React.useMemo(
        () => mainPhotos.filter( (photo) => selectedIds.has(photo.id) ),
        [mainPhotos, selectedIds]
    );
    const unselectedPhotos = React.useMemo(
        () => mainPhotos.filter( (photo) => !selectedIds.has(photo.id) ),
        [mainPhotos, selectedIds]
    );

    // Başka bir oda açıldığında (yeni liste geldiğinde) seçim ve manuel sıra API verisine göre kurulur
    React.useEffect(() => {
        const initialPhotos = roomPhotoList && roomPhotoList.get_room_photos ? roomPhotoList.get_room_photos : EMPTY_PHOTOS;

        const selected = initialPhotos.filter( (photo) => photo.is_selected === true );

        setSelectedIds(new Set( selected.map( (photo) => photo.id ) ));

        // Kaydedilmiş sıra sort_order'dan yüklenir; sort_order'ı olmayan seçili fotoğraflar
        // photoOrder'a girmez, orderedSelectedPhotos'ta sona eklenir (mevcut mantık).
        setPhotoOrder(
            selected
                .filter( (photo) => photo.sort_order !== null && photo.sort_order !== undefined )
                .sort( (a, b) => a.sort_order - b.sort_order )
                .map( (photo) => photo.id )
        );
    }, [roomPhotoList]);

    // photoOrder'daki sıra uygulanır; listede olmayan id'ler elenir,
    // sırası belirlenmemiş seçili fotoğraflar sona eklenir
    const orderedSelectedPhotos = React.useMemo(() => {
        const photosById = new Map(selectedPhotos.map( (photo) => [photo.id, photo] ));
        const ordered = photoOrder.map( (id) => photosById.get(id) ).filter(Boolean);
        const orderedIds = new Set(ordered.map( (photo) => photo.id ));

        return ordered.concat(selectedPhotos.filter( (photo) => !orderedIds.has(photo.id) ));
    }, [selectedPhotos, photoOrder]);

    // Seçili ve seçili olmayanlar TEK listede render edilir. İki ayrı map olsaydı fotoğraf
    // grup değiştirirken unmount/mount olur, DOM düğümü yok olacağı için animasyon yapılamazdı.
    const displayPhotos = React.useMemo(
        () => orderedSelectedPhotos.concat(unselectedPhotos),
        [orderedSelectedPhotos, unselectedPhotos]
    );

    const photoGridRef = React.useRef(null);
    useFlipOnReorder(photoGridRef, selectedIds);

    const handleDragEnd = (event, roomId) => {
        const {active, over} = event;

        if( !over || active.id === over.id ){
            return;
        }

        const currentIds = orderedSelectedPhotos.map( (photo) => photo.id );
        const oldIndex = currentIds.indexOf(active.id);
        const newIndex = currentIds.indexOf(over.id);

        if( oldIndex === -1 || newIndex === -1 ){
            return;
        }

        const nextOrder = arrayMove(currentIds, oldIndex, newIndex);

        setPhotoOrder(nextOrder);
        saveRoomPhotoOrder(roomId, nextOrder);
    };

    // Sürükleme bitince o odadaki seçili fotoğrafların tamamını sıralı (1..N) olarak backend'e yazar.
    const saveRoomPhotoOrder = (roomId, orderedIds) => {
        const data = ({
            params: {
                property_id: getProperty(),
                room_id: roomId,
                property_room_photo: orderedIds.map( (id, index) => (
                    { id: +id, sort_order: index + 1 }
                ))
            }
        });

        axios.post(ROOM_PHOTO_ORDER, data,{
            headers: {
                "Accept": "application/json",
                authToken: getUserToken(),
                "Content-Type": "application/json",
                "language": getAppLang(),
            }
        }).then((result) => {
        })
            .catch(function (error) {
                console.log(error);
            });
    };

    const handleChange = (roomId) => (event, isExpanded) => {
        setExpanded(isExpanded ? roomId : false);
        if(isExpanded !== false){
            getRoomPhotoList(roomId);
        }
    };

    // Ana listedeki fotoğrafın seçimi değişti: state'i güncelle + API'ye bildir.
    // Seçilen fotoğraf sıralanabilir grubun sonuna eklenir, seçimi kaldırılan alt gruba düşer.
    const handlePhotoToggle = (e, roomId) => {
        // React 16 event pooling: değerler updater'a girmeden önce okunmalı
        const photoValue = e.target.value;
        const isChecked  = e.target.checked;

        if( photoValue === undefined || isChecked === undefined ){
            return;
        }

        const toggledIds = String(photoValue).split(',').map( (x) => +x );

        setSelectedIds( (prevSelectedIds) => {
            const nextSelectedIds = new Set(prevSelectedIds);

            toggledIds.forEach( (id) => {
                if(isChecked){
                    nextSelectedIds.add(id);
                }else{
                    nextSelectedIds.delete(id);
                }
            });

            return nextSelectedIds;
        });

        updateRoomPhoto(e, roomId);
    };

    // Seçili fotoğraflar üstte durur ve yalnızca kendi aralarında sürüklenerek sıralanır;
    // seçili olmayanlar altta sabit kalır.
    const RoomPhotoList = ( roomId ) => {

        if(roomPhotoList.length === 0 ){
            return;
        }

        if( expanded === false || roomPhotoListFetching !== false ){
            return;
        }

        return (
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={ (event) => handleDragEnd(event, roomId) }
            >
                <SortableContext
                    items={ displayPhotos.map( (photo) => photo.id ) }
                    strategy={rectSortingStrategy}
                >
                    {
                        displayPhotos.map( (photo) => {
                            const isSelected = selectedIds.has(photo.id);

                            return (
                                <SortablePhotoSelect
                                    key={photo.id}
                                    sortableId={photo.id}
                                    sortableDisabled={!isSelected}
                                    checkboxId={photo.id}
                                    className="channel-box"
                                    colClassname={ isSelected ? "room-photo-grid sortable-photo" : "room-photo-grid" }
                                    blockClass="photo-select-border-box channel-border-box"
                                    src={ photo.photo_url_thump}
                                    value={photo.id}
                                    checked={isSelected}
                                    onChange={ (e) => handlePhotoToggle(e,roomId)}
                                    labelClass="fact-name"
                                    htmlFor={photo.id}
                                />
                            );
                        })
                    }
                </SortableContext>
            </DndContext>
        );
    };

    const RoomRecommendedPhotoList = ( roomId ) => {

        if(roomPhotoList.length === 0 ){
            return;
        }
        let roomPhotoListBox = '';
        if( expanded !== false && roomPhotoListFetching === false  && roomPhotoList.show_recommended === true){

            roomPhotoListBox = roomPhotoList.get_room_photos.map( (photo, key) => (

                photo.is_recommended === true ? (
                    <MultiPhotoSelect
                        checkboxId={photo.id}
                        className="channel-box"
                        colClassname="room-photo-grid"
                        blockClass="photo-select-border-box channel-border-box"
                        src={ photo.photo_url_thump}
                        value={photo.id}
                        defaultChecked={photo.is_selected}
                        onClick={ (e) => updateRoomPhoto(e,roomId)}
                        labelClass="fact-name"
                        htmlFor={photo.id}
                    />
                ) : (
                        <></>
                    )
                )
            );

            return roomPhotoListBox;
        }

    };

    const updateRoomPhoto = (e,roomId) => {
        const photoId      = e.target.value
        const photoChecked = e.target.checked

        if( photoId === undefined && photoChecked === undefined ) {
            return;
        }

        const url = ROOM_PHOTO_UPDATE;

        const data = ({
            params: {
                property_id: getProperty(),
                room_id: roomId,
                property_room_photo: photoId.split(',').map( (x) => (
                    { id :+x,
                        is_selected:  e.target.checked}
                ))
            }
        });

        axios.post(url, data,{
            headers: {
                "Accept": "application/json",
                authToken: getUserToken(),
                "Content-Type": "application/json",
                "language": getAppLang(),
            }
        }).then((result) => {
        })
            .catch(function (error) {
                console.log(error);
            });
    };

    if(rooms && rooms.length === 0 )
    {
        return (
            <h2>{ t('enw-notification-no_results') }</h2>
        )
    }else{
        return (

            <>
                {
                    props.selectedRoomTypeId  === 0 ? (
                            props.rooms.map( (room, key) => (

                                    <ExpansionPanel className={room?.status === 1 ? "accordion-room-table" : "accordion-room-table inactive-plcs"} key={room.id} expanded={expanded === room.id} onChange={handleChange(room.id)}>
                                        <ExpansionPanelSummary
                                            expandIcon={<i className="fas fa-caret-down" />}
                                            aria-label="Expand">
                                            <ColAtm>
                                                <ColAtm md={10} xl={11}>
                                                    <i className="fas fa-bed room-rate-bed" />
                                                    <LabelAtm labelClass="room-rate-list-title" text={room.name} />
                                                </ColAtm>
                                            </ColAtm>
                                        </ExpansionPanelSummary>
                                        <ExpansionPanelDetails className="room-rate-detail MuiGrid-root MuiGrid-container MuiGrid-item">
                                            <ColAtm>
                                                {
                                                    roomPhotoList.show_recommended === true && <ColAtm className="recommended-box">
                                                        <ColAtm xs={6} md={2}>
                                                            <LabelAtm text={ t('enw-room-recommended') } />
                                                        </ColAtm>
                                                        <CheckboxGroup>
                                                            <ColAtm xs={6} md={10} className="select-all">
                                                                <label className="select-all-chck">
                                                                    <AllCheckerCheckbox
                                                                        value={[
                                                                            roomPhotoList.get_room_photos.map( (photo, key) => (
                                                                                photo.is_recommended === true && photo.is_recommended != "false"  && photo.is_recommended != false ?
                                                                                    [photo.id + (",")]
                                                                                    :
                                                                                    null
                                                                            )).join('').slice(0, -1)
                                                                        ]}
                                                                        onChange={ (e) => updateRoomPhoto(e, room.id)}
                                                                    />
                                                                    <span className="select-all-text">
                                                                        { t('btn-deselect_all') }
                                                                    </span>
                                                                    <span className="deselect-all-text">
                                                                        { t('btn-select_all') }
                                                                    </span>
                                                                </label>
                                                            </ColAtm>
                                                            { expanded === room.id ? RoomRecommendedPhotoList(room.id) : '' }
                                                        </CheckboxGroup>
                                                    </ColAtm>
                                                }
                                                <ColAtm innerRef={ expanded === room.id ? photoGridRef : undefined }>
                                                    { expanded === room.id ? RoomPhotoList(room.id) : '' }
                                                </ColAtm>
                                            </ColAtm>
                                        </ExpansionPanelDetails>
                                    </ExpansionPanel>
                                ))
                    ) : (
                        props.rooms.filter( (room) => room.room_type_id === props.selectedRoomTypeId )
                            .map( (room, key) => (

                                <ExpansionPanel className={room?.status === 1 ? "accordion-room-table" : "accordion-room-table inactive-plcs"} key={room.id} expanded={expanded === room.id} onChange={handleChange(room.id)}>
                                    <ExpansionPanelSummary
                                        expandIcon={<i className="fas fa-caret-down" />}
                                        aria-label="Expand">
                                        <ColAtm>
                                            <ColAtm md={10} xl={11}>
                                                <i className="fas fa-bed room-rate-bed" />
                                                <LabelAtm labelClass="room-rate-list-title" text={room.name} />
                                            </ColAtm>
                                        </ColAtm>
                                    </ExpansionPanelSummary>
                                    <ExpansionPanelDetails className="room-rate-detail MuiGrid-root MuiGrid-container MuiGrid-item">
                                        <ColAtm>
                                            {
                                                roomPhotoList.show_recommended === true && <ColAtm className="recommended-box">
                                                    <ColAtm xs={6} md={2}>
                                                        <LabelAtm text={ t('enw-room-recommended') } />
                                                    </ColAtm>
                                                    <CheckboxGroup>
                                                        <ColAtm xs={6} md={10} className="select-all">
                                                            <label className="select-all-chck">
                                                                <AllCheckerCheckbox
                                                                    value={[
                                                                        roomPhotoList.get_room_photos.map( (photo, key) => (
                                                                            photo.is_recommended === true && photo.is_recommended != "false"  && photo.is_recommended != false ?
                                                                                [photo.id + (",")]
                                                                                :
                                                                                null
                                                                        )).join('').slice(0, -1)
                                                                    ]}
                                                                    onChange={ (e) => updateRoomPhoto(e, room.id)}
                                                                />
                                                                <span className="select-all-text">
                                                                        { t('btn-select_all') }
                                                                    </span>
                                                                <span className="deselect-all-text">
                                                                        { t('btn-deselect_all') }
                                                                    </span>
                                                            </label>
                                                        </ColAtm>
                                                        { expanded === room.id ? RoomRecommendedPhotoList(room.id) : '' }
                                                    </CheckboxGroup>
                                                </ColAtm>
                                            }
                                            <ColAtm>
                                                { expanded === room.id ? RoomPhotoList(room.id) : '' }
                                            </ColAtm>
                                        </ColAtm>
                                    </ExpansionPanelDetails>
                                </ExpansionPanel>
                        ))
                    )
                }

            </>
        );
    }


};

RoomList.propTypes = {
    rooms: PropTypes.array.isRequired
};
RoomList.defaultProps = {};

export default RoomList;
